import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Code,
  Storage,
  Cloud,
  PhoneIphone,
  Speed,
  Info
} from '@mui/icons-material';
import { FadeInSection } from '../Animations/FadeInAnimation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tech-tabpanel-${index}`}
      aria-labelledby={`tech-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const techStack = {
  frontend: [
    { name: 'React & Next.js', level: 95, description: 'Modern web applications with SSR/SSG capabilities' },
    { name: 'TypeScript', level: 90, description: 'Type-safe development across the stack' },
    { name: 'Angular', level: 85, description: 'Enterprise-level applications' },
    { name: 'Vue.js', level: 80, description: 'Lightweight and progressive frameworks' }
  ],
  backend: [
    { name: 'Node.js', level: 92, description: 'Scalable server-side applications' },
    { name: 'Python', level: 88, description: 'Data processing and API development' },
    { name: 'Java Spring', level: 85, description: 'Enterprise backend solutions' },
    { name: 'Go', level: 78, description: 'High-performance microservices' }
  ],
  mobile: [
    { name: 'React Native', level: 90, description: 'Cross-platform mobile development' },
    { name: 'iOS (Swift)', level: 85, description: 'Native iOS applications' },
    { name: 'Android (Kotlin)', level: 85, description: 'Native Android applications' },
    { name: 'Flutter', level: 80, description: 'Multi-platform app development' }
  ],
  cloud: [
    { name: 'AWS', level: 92, description: 'Cloud infrastructure and services' },
    { name: 'Azure', level: 88, description: 'Enterprise cloud solutions' },
    { name: 'Google Cloud', level: 85, description: 'Cloud-native applications' },
    { name: 'Kubernetes', level: 90, description: 'Container orchestration' }
  ],
  databases: [
    { name: 'PostgreSQL', level: 90, description: 'Relational database systems' },
    { name: 'MongoDB', level: 88, description: 'NoSQL document databases' },
    { name: 'Redis', level: 85, description: 'In-memory data structures' },
    { name: 'Elasticsearch', level: 82, description: 'Search and analytics' }
  ],
  devops: [
    { name: 'Docker', level: 92, description: 'Containerization' },
    { name: 'Jenkins', level: 88, description: 'Continuous Integration' },
    { name: 'GitLab CI', level: 85, description: 'DevOps automation' },
    { name: 'Terraform', level: 80, description: 'Infrastructure as Code' }
  ]
};

const categories = [
  { label: 'Frontend', icon: <Code />, key: 'frontend' },
  { label: 'Backend', icon: <Storage />, key: 'backend' },
  { label: 'Mobile', icon: <PhoneIphone />, key: 'mobile' },
  { label: 'Cloud', icon: <Cloud />, key: 'cloud' },
  { label: 'Databases', icon: <Storage />, key: 'databases' },
  { label: 'DevOps', icon: <Speed />, key: 'devops' }
];

export const TechExpertise = () => {
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log(event)
    setValue(newValue);
  };

  return (
    <FadeInSection delay={0.6}>
        <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom>
              Technology Expertise
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Cutting-edge technologies for modern software solutions
            </Typography>
          </Box>

          <Paper 
            elevation={0}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 64,
                  fontSize: '1rem'
                }
              }}
            >
              {categories.map((category, index) => (
                <Tab
                  key={index}
                  icon={category.icon}
                  label={category.label}
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center'
                  }}
                />
              ))}
            </Tabs>

            {categories.map((category, index) => (
              <TabPanel key={index} value={value} index={index}>
                <Grid container spacing={3}>
                  {techStack[category.key as keyof typeof techStack].map((tech, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          height: '100%',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">
                              {tech.name}
                            </Typography>
                            <Tooltip title={tech.description} arrow>
                              <IconButton size="small">
                                <Info fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ flexGrow: 1, mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={tech.level}
                                sx={{
                                  height: 8,
                                  borderRadius: 1,
                                  backgroundColor: 'background.default',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 1
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {tech.level}%
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {tech.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>
            ))}
          </Paper>
        </Container>
      </Box>

    </FadeInSection>
    
  );
};

